import React from 'react';
import { DateTime, Duration } from 'luxon';
import { CloseOutlined, ExclamationOutlined } from '@ant-design/icons';

import * as unitMagic from '@metservicedev/cf-units';

import { TafRow, elements } from '@mono/types/src/weather-widgets/threat-matrix';
import { CdsTafResponseData } from '@mono/use-cds-taf';
import { getIcon } from '@mono/ww-threat-matrix-icons';

import { generateThresholdStatus, getThresholdOperator } from './thresholds';
import { Cell } from './types';
import styled from 'styled-components';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

type generateCellsForTafProps = {
    cdsTafData: { [icaoId: string]: CdsTafResponseData };
    row: TafRow;
    timeRange: string;
};

const RowLabel = styled.span<{ mode?: 'cozy' | 'compact' }>`
    flex: 1;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
`;

const ThresholdLabel = styled.span<{ mode?: 'cozy' | 'compact' }>`
    width: ${({ mode }) => (mode === 'cozy' ? '70px' : '60px')};
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p};
`;

export const generateCellsForTaf = (props: generateCellsForTafProps): Cell[] => {
    const { cdsTafData, row, timeRange } = props;

    const emptyRow: Cell[] = [
        {
            status: undefined,
            label: undefined,
            width: 1,
            isObs: true,
        },
        ...Array.from({ length: Duration.fromISO(timeRange.split('--')[1]).as('hours') }, (_, i) => {
            return { status: undefined, label: undefined, width: 1 };
        }),
    ];

    if (!cdsTafData) return emptyRow;
    if (!row.icaoId) return emptyRow;
    const dataForIcaoId = cdsTafData[row.icaoId];
    if (!dataForIcaoId) return emptyRow;
    const numberOfHours = Duration.fromISO(timeRange.split('--')[1]).as('hours');
    const hourKeys = Array.from({ length: numberOfHours }).map((_, i) => {
        return DateTime.utc().startOf('hour').plus({ hours: i }).toISO();
    });

    const metarObject = Object.values(dataForIcaoId).find((v) => {
        return v.dataSource === 'METAR';
    });

    if (!row.cdsTafVariables) return [];

    const obsStatus = metarObject
        ? generateThresholdStatus({
              thresholds: row.cdsTafVariables
                  .map((cdsTafVariable) => {
                      const value = metarObject[cdsTafVariable.cdsTafVariableKey];
                      if (!value) return;
                      if (!row.element) return;
                      const defaultCdsTafVariables = elements[row.element].cdsTafVariables;
                      const defaultCdsTafVariable = defaultCdsTafVariables.find((d) => d.cdsTafVariableKey === cdsTafVariable.cdsTafVariableKey);
                      if (!defaultCdsTafVariable) return;
                      const unitConverted =
                          defaultCdsTafVariable.unit && cdsTafVariable.unit
                              ? unitMagic.convertValueUnit({ from: defaultCdsTafVariable.unit, to: cdsTafVariable.unit, value })
                              : value;

                      return {
                          thresholdOperator: cdsTafVariable.operator,
                          warningThreshold: cdsTafVariable.warning,
                          alertThreshold: cdsTafVariable.alert,
                          value: Number(unitConverted),
                      };
                  })
                  .filter((t): t is NonNullable<typeof t> => !!t),
          })
        : undefined;

    const obsCell: Cell = {
        status: obsStatus,
        width: 1,
        isObs: true,
    };
    const cells = hourKeys.reduce<Cell[]>((cells, time) => {
        if (!row.element) return [...cells, { status: undefined, label: undefined, width: 1 }];
        if (!row.cdsTafVariables) return [...cells, { status: undefined, label: undefined, width: 1 }];

        const status = generateThresholdStatus({
            thresholds: row.cdsTafVariables
                .map((cdsTafVariable) => {
                    const value = dataForIcaoId[time]?.[cdsTafVariable.cdsTafVariableKey];
                    if (!value) return;
                    if (!row.element) return;

                    const defaultCdsTafVariables = elements[row.element].cdsTafVariables;
                    const defaultCdsTafVariable = defaultCdsTafVariables.find((d) => d.cdsTafVariableKey === cdsTafVariable.cdsTafVariableKey);
                    if (!defaultCdsTafVariable) return;
                    const unitConverted =
                        defaultCdsTafVariable.unit && cdsTafVariable.unit
                            ? unitMagic.convertValueUnit({ from: defaultCdsTafVariable.unit, to: cdsTafVariable.unit, value })
                            : value;

                    return {
                        thresholdOperator: cdsTafVariable.operator,
                        warningThreshold: cdsTafVariable.warning,
                        alertThreshold: cdsTafVariable.alert,
                        value: Number(unitConverted),
                    };
                })
                .filter((t): t is NonNullable<typeof t> => !!t),
        });
        if (status === undefined) {
            return [
                ...cells,
                {
                    status: undefined,
                    label: undefined,
                    width: 1,
                },
            ];
        }

        return [
            ...cells,
            {
                status,
                label: DateTime.fromISO(time).toISO(),
                width: 1,
            },
        ];
    }, []);
    return [obsCell, ...cells];
};

interface TafRowHeaderProps {
    row: TafRow;
    mode: 'cozy' | 'compact';
}
export const TafRowHeader = ({ row, mode }: TafRowHeaderProps) => {
    const primaryCdsTafVariableKey = row.element ? elements[row.element].primaryCdsTafVariableKey : undefined;
    const primaryCdsTafVariable = row.cdsTafVariables?.find((a) => a.cdsTafVariableKey === primaryCdsTafVariableKey);

    const icon = row.element ? elements[row.element].icon : undefined;
    const Icon = getIcon(icon);

    const thresholdOperator = primaryCdsTafVariable && getThresholdOperator(primaryCdsTafVariable.operator);
    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingRight: 8, gap: 8, width: '100%' }}>
            {Icon && <Icon size={TextStyles[mode ?? 'cozy'].iconSize} />}
            <RowLabel mode={mode}>{row.label}</RowLabel>

            {primaryCdsTafVariable && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ExclamationOutlined style={{ color: ' #EE7734', fontSize: 'large' }} />
                        <ThresholdLabel mode={mode}>
                            <span style={{ marginLeft: 2.5, marginRight: 2.5 }}>{thresholdOperator}</span>
                            <span>{primaryCdsTafVariable.warning}</span>
                        </ThresholdLabel>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CloseOutlined style={{ color: '#D62828', fontSize: 'large' }} />
                        <ThresholdLabel mode={mode}>
                            <span style={{ marginLeft: 2.5, marginRight: 2.5 }}>{thresholdOperator}</span>
                            <span>{primaryCdsTafVariable.alert}</span>
                        </ThresholdLabel>
                    </div>
                </>
            )}
        </div>
    );
};
